/** Bill checks, from the browser's side: advisory flags on the bills behind a figure.
 *
 * A spike against last year, the same bill twice, a unit that changed, a supply that went quiet. The
 * server derives them on every request from the figures in force (`reporting/bill_checks.py`), stores
 * nothing and edits nothing, so this client holds nothing either: it asks, and a correction elsewhere is
 * seen on the next read (the key sits under the record's own, so a write that refreshes the record refreshes this).
 *
 * **A failed read is not "no flags".** An empty list says every check ran and found nothing; a dropped
 * request must not say that, so a failure throws and the section says it could not check. A 404 is the one
 * answer that means *nothing to show you* (no such record here, or outside your engagement) and is `null`.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type BillChecks = components["schemas"]["BillChecksOut"];
export type BillCheck = components["schemas"]["BillCheckOut"];
export type BillCheckSide = components["schemas"]["BillCheckSideOut"];

export const billCheckKeys = {
  /** Under `["activity", id]`, so whatever refreshes a record refreshes its checks with it. */
  record: (recordId: string) => ["activity", recordId, "bill-checks"] as const,
};

export function useRecordBillChecks(recordId: string) {
  return useQuery({
    queryKey: billCheckKeys.record(recordId),
    retry: false,
    queryFn: async (): Promise<BillChecks | null> => {
      const { data, error, response } = await api.GET("/api/v1/activities/{activity_id}/bill-checks", {
        params: { path: { activity_id: recordId } },
      });
      if (response.status === 404) return null;
      if (error) throw refusalFrom(response, error, "The bill checks could not be read.");
      if (!data || !Array.isArray(data.items)) throw new Error("The bill checks could not be read.");
      return data;
    },
  });
}
